/**
 * THE RELATIONSHIP-AUTHORING WORKSPACE (#2351).
 *
 * One surface for the whole act: choose the relationship type, find or create a
 * target, inspect it, supply the facts the EDGE carries, and commit — without
 * losing the type, the search, the basket or the draft on the way.
 *
 * ── what it replaces, and why the shape had to change ──────────────────────
 * Three surfaces and two stacked modals: `RelationshipAddChooser` (a 520px
 * chooser with one select and two buttons), then either `RelationAdd`'s Lookup
 * dialog plus a SECOND modal for edge facts, or a jump out to the shell
 * drawer's quick-create. The type choice was thrown away the moment it was
 * used, nothing could hold more than one target, and the edge facts arrived in
 * a dialog stacked over a dialog.
 *
 * ── everything governed stays governed ─────────────────────────────────────
 * This component decides nothing a server already decides. It reads:
 *
 *   · the SLOTS from the metamodel (`deriveRelationSlots`, #484);
 *   · the RAIL's grouping and wording from the entity's version-4
 *     table-presentation document (`picker`, core 2026091721) — absent, the
 *     rail is the derived order under one ungrouped heading, and no Moonbase
 *     category name appears in this file;
 *   · the ICON and the singular label from `core.entity_identity`, and
 *     NAVIGATION ELIGIBILITY from its `non_record_surface`;
 *   · the COUNTS from `core.related_counts`, where an ABSENT slot is unknown
 *     and never drawn as zero;
 *   · the CANDIDATES from a registered `search_*` door or a server-side ilike
 *     (`useRelationCandidates`), bounded and probed, never a client-side pool;
 *   · the PREVIEW from the target's own canonical form through the governed
 *     Peek lens (`PeekRow`), which is the record page's own resolution;
 *   · the CREATE FORM from the same derivation the drawer uses, with the same
 *     duplicate-person guard;
 *   · the EDGE FACTS from the relationship type's declared attributes.
 *
 * And it writes through `core.mutate_relationship` and nothing else. The
 * dispatcher takes ONE EDGE PER CALL, so `Link N records` is N calls and a
 * partial failure is reported as one (`batchSentence`); it is never described
 * as atomic. `Create & link` is the dispatcher's own atomic create-and-link.
 *
 * The UI never manufactures an inverse edge. It may SHOW the inverse reading —
 * that is what `inboundLabel` resolves — and if a relationship's contract
 * governs a reciprocal, the door performs it transactionally.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Info, Plus, Search, X } from 'lucide-react'
import type { GraphHop } from '@solum/experience'
import { pickerGroupOrder, pickerSlotPresentation, PICKER_UNGROUPED } from '@solum/experience'
import type { Row } from '@solum/sdk'
import {
  Button, Chip, CountPill, EmptyState, EntityIcon, Field, FilterChip, Form, IconTile, ListRow,
  Modal, Notice, OptionPicker, Pill, SearchField, SegmentedControl, Steps, useShellCompact,
} from '@solum/ui'
import { useSolum } from '../data/client'
import { displayLabel, useEntityIdentities, useSchema, useSemantics, useTablePresentation } from '../data/queries'
import { PeekRow } from './PeekRow'
import { splitRelatedDraft, wireRelations } from './QuickCreatePanel'
import { decideRelatedCreate, personIdentityFingerprint, type PersonCreateReview } from './personCreateGuard'
import { relationshipWriteOffered, type RelationSlot } from './relations'
import { CANDIDATE_SORT_LABELS, slotMaxSelection, type CandidateSort } from './relationCandidates'
import { useCandidateRequest, useRelationCandidates } from './useRelationCandidates'
import { useRelatedPresentation } from './useRelatedPresentation'
import {
  batchSentence, commitLabel, commitReady, EMPTY_WORKSPACE, WORKSPACE_STAGES,
  normaliseStage, stagedPrimary, workspaceReducer,
  type BatchOutcome, type WorkspaceStage,
} from './relationshipWorkspaceModel'
import {
  countLabel, quickCreateFields, relationshipAttributeFields,
  useCreateRelated, useLinkRelation, useRelatedCounts, useRelationSearch, useRelationTargetFields,
} from './useRelations'
import s from './RelationshipWorkspace.module.css'
import { readableError } from '@solum/sdk'

/**
 * Below this width the workspace is a STAGED flow — type, then target, then
 * review — because three panes in 390px is three unusable panes.
 *
 * Local rather than shared, for the reason `packages/ui/src/shell/breakpoints.ts`
 * gives about `WorkspaceSplit`: this answers a different question from the
 * shell's compact boundary (which this file also uses, for the preview sheet),
 * and binding them would couple a dialog's staging to the chrome. The literal
 * lives in the stylesheet too, and `RelationshipWorkspace.stage.test.ts` fails
 * if the two drift.
 */
export const WORKSPACE_STAGED_MAX_PX = 600
const STAGED_QUERY = `(max-width: ${WORKSPACE_STAGED_MAX_PX}px)`

/** What each staged step is called — the words the kit stepper names its
    nodes with. Visually the compact stepper shows numbers; a screen reader
    hears these. */
const STAGE_LABEL: Record<WorkspaceStage, string> = {
  type: 'Relationship type',
  target: 'Records',
  review: 'Review',
}

function useStaged(): boolean {
  const [staged, setStaged] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(STAGED_QUERY).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(STAGED_QUERY)
    const onChange = () => setStaged(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return staged
}

/** One rail group, as the rail draws it. */
interface RailGroup {
  key: string
  label: string
  slots: { slot: RelationSlot; description: string | null; icon: string | null }[]
}

/**
 * The rail, grouped by the governed document and ordered by it.
 *
 * A slot the document says nothing about lands in the UNGROUPED bucket, which
 * is drawn without a heading — and with no document at all that is every slot,
 * in the order `deriveRelationSlots` produced (lists before single-value
 * fields, identity edges last, then label). The groups are never reordered
 * locally and never invented.
 */
export function railGroups(
  slots: readonly RelationSlot[],
  picker: Parameters<typeof pickerGroupOrder>[0],
): RailGroup[] {
  const declaredOrder = pickerGroupOrder(picker)
  const byKey = new Map<string, RailGroup>()
  for (const group of declaredOrder) byKey.set(group.key, { ...group, slots: [] })
  byKey.set(PICKER_UNGROUPED, { key: PICKER_UNGROUPED, label: '', slots: [] })

  for (const slot of slots) {
    const declared = pickerSlotPresentation(picker, slot.id, slot.otherEntity)
    const group = declared?.group && byKey.has(declared.group) ? declared.group : PICKER_UNGROUPED
    byKey.get(group)!.slots.push({
      slot,
      description: declared?.description ?? null,
      icon: declared?.icon ?? null,
    })
  }
  return [...byKey.values()].filter((group) => group.slots.length > 0)
}

export interface RelationshipWorkspaceProps {
  /** Every slot a record of this entity can be given more of. */
  slots: readonly RelationSlot[]
  entity: string
  recordId: string
  recordLabel?: string
  onClose: () => void
  /** Open straight onto one slot — ⌘K's verb, and a band's own Add. */
  initialSlot?: string | null
}

export function RelationshipWorkspace({
  slots, entity, recordId, recordLabel, onClose, initialSlot = null,
}: RelationshipWorkspaceProps) {
  const [state, dispatch] = useReducer(workspaceReducer, {
    ...EMPTY_WORKSPACE,
    slot: initialSlot,
    stage: initialSlot ? 'target' : 'type',
  })
  const [typeQuery, setTypeQuery] = useState('')
  const [review, setReview] = useState<PersonCreateReview | null>(null)
  const [guardError, setGuardError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  /* A div now: the announcement is the shared `Notice`, not a paragraph. */
  const errorRef = useRef<HTMLDivElement>(null)
  const createFormRef = useRef<HTMLFormElement>(null)
  /** The info glyph the preview sheet was opened from, for focus restoration. */
  const previewTrigger = useRef<HTMLButtonElement | null>(null)
  /* WHICH button asked, so the create path can go through the form's own
     validation gate and still know whether to stay open. A ref rather than
     state: it is read inside the submit handler the form calls, not rendered. */
  const pendingAnother = useRef(false)

  const staged = useStaged()
  const compact = useShellCompact()
  const presentation = useTablePresentation(entity)
  const identities = useEntityIdentities()
  const counts = useRelatedCounts(entity, recordId)

  const slot = useMemo(() => slots.find((s) => s.id === state.slot) ?? null, [slots, state.slot])
  const groups = useMemo(() => railGroups(slots, presentation.document.picker), [slots, presentation.document.picker])
  const shownGroups = useMemo(() => {
    const q = typeQuery.trim().toLowerCase()
    if (!q) return groups
    return groups
      .map((group) => ({
        ...group,
        slots: group.slots.filter(({ slot: candidate, description }) =>
          candidate.label.toLowerCase().includes(q)
          || candidate.otherLabel.toLowerCase().includes(q)
          || (description ?? '').toLowerCase().includes(q)),
      }))
      .filter((group) => group.slots.length > 0)
  }, [groups, typeQuery])

  const identity = useMemo(
    () => identities.data?.find((e) => e.entity_type === slot?.otherEntity) ?? null,
    [identities.data, slot?.otherEntity])
  /* NAVIGATION ELIGIBILITY IS GOVERNED DATA (#1993). `non_record_surface`
     carries the SENTENCE saying why a surface is not a navigable record — a
     ledger, a join row, an append-only evidence table — so "Open in new tab"
     is offered exactly where there is a record to open. A boolean inferred from
     "it has no title" is the gap that issue exists about. */
  const navigable = identity != null && !identity.non_record_surface

  const offered = {
    link: slot != null && relationshipWriteOffered(slot, 'link'),
    create: slot != null && relationshipWriteOffered(slot, 'create'),
  }
  const mode = state.mode === 'create' && offered.create ? 'create' : 'link'
  const max = slot ? slotMaxSelection(slot) : null

  const { request, set, more, reset } = useCandidateRequest()
  const candidates = useRelationCandidates(mode === 'link' ? slot : null, recordId, {
    ...request, sort: state.sort,
  })
  const fields = useRelationTargetFields(slot)
  const edgeFields = useMemo(() => edgeFactFields(slot), [slot])
  const link = useLinkRelation(slot, recordId)
  const create = useCreateRelated(slot, recordId)
  const directorySearch = useRelationSearch(slot)

  /* The governed Peek recipe for THIS slot — the same resolution the record
     page's own Peek lens uses, asked for the one slot on screen. */
  const slotRefs = useMemo(
    () => (slot ? [{ slot: slot.id, target: slot.otherEntity }] : []),
    [slot])
  const related = useRelatedPresentation(entity, slotRefs, {})
  const peek = slot ? related.of(slot.id) : null

  // Switching type or mode resets what is being asked, so the widened window
  // and the typed query do not follow the person onto a different entity.
  useEffect(() => { reset() }, [state.slot, mode, reset])

  useEffect(() => {
    if (state.announcement && errorRef.current) errorRef.current.focus()
  }, [state.announcement])

  const basketIds = useMemo(() => new Set(state.basket.map((e) => e.id)), [state.basket])
  /* THE NORMALISED step, never the stored one: records without an eligible type
     is the type step and review without a basket or a draft is the records
     step, whichever way the state arrived there (ROOT's ruling A). */
  const stage: WorkspaceStage = normaliseStage(state)
  const primary = stagedPrimary(state, offered)

  const commit = async (addAnother: boolean) => {
    if (!slot) return
    setGuardError(null)
    setBusy(true)
    try {
      if (mode === 'create') {
        const parts = splitRelatedDraft(state.draft)
        const decision = await decideRelatedCreate(slot.otherEntity, parts.record, directorySearch, review?.fingerprint)
        if (decision.kind === 'review') { setReview(decision); return }
        const made = await create.mutateAsync({
          record: parts.record,
          edgeAttributes: { ...edgeAttributes(state.edge, edgeFields), ...parts.edgeAttributes },
        })
        setReview(null)
        /* Named from what came BACK, not from the draft: the server owns the
           row, and a sentence built from the form would claim a name the
           database may have normalised. With no name to resolve it says what
           happened without inventing one — and never the uuid. */
        const madeLabel = displayLabel((made ?? {}) as Row, identity?.singular_label ?? null)
        dispatch({
          kind: 'committed',
          message: `Created and linked ${madeLabel}.`,
          keepOpen: addAnother,
        })
        if (!addAnother) onClose()
        return
      }
      /* N CALLS, because the dispatcher takes one edge each — and the basket
         keeps exactly what did not land, so the person can retry the failures
         without re-finding them. Sequential rather than parallel: a refusal on
         the third should not have fired the fourth. */
      const outcome: BatchOutcome = { linked: [], failed: [] }
      for (const entry of state.basket) {
        try {
          await link.mutateAsync({
            otherId: entry.id,
            edgeAttributes: edgeAttributes(state.edge, edgeFields),
            validFrom: (state.edge.valid_from as string | undefined) || null,
          })
          outcome.linked.push(entry)
        } catch (e) {
          outcome.failed.push({ entry, message: readableError(e, 'That link was refused.') })
        }
      }
      const message = batchSentence(outcome)
      if (outcome.failed.length > 0) {
        /* THE WORKSPACE STAYS OPEN. A refusal is what this surface exists to
           recover from, so the basket keeps the failures, the query and the
           preview are untouched, and the message is focused and announced. */
        dispatch({ kind: 'refused', message })
        for (const done of outcome.linked) dispatch({ kind: 'remove', id: done.id })
        return
      }
      dispatch({ kind: 'committed', message, keepOpen: addAnother })
      if (!addAnother) onClose()
    } catch (e) {
      dispatch({ kind: 'refused', message: readableError(e, 'That could not be saved.') })
    } finally {
      setBusy(false)
    }
  }

  const createFields = useCreateFormFields(mode === 'create' ? slot : null, fields, state.draft)

  /* ONE primary action, two paths to it. Creating goes through the form so its
     required fields are checked by the rule that owns them; linking has no form
     to validate and commits directly. */
  const submit = (addAnother: boolean) => {
    pendingAnother.current = addAnother
    if (mode === 'create') {
      createFormRef.current?.requestSubmit()
      return
    }
    void commit(addAnother)
  }

  const highlightedCandidate = useMemo(
    () => candidates.items.find((c) => c.id === state.highlighted) ?? null,
    [candidates.items, state.highlighted])

  const previewPane = (
    <PreviewPane
      slot={slot}
      id={state.highlighted}
      /* THE LABEL TRAVELS. Without it the preview's Add would have to name the
         record from the id it holds, which is the uuid-as-a-label defect this
         lane fixed two places over. No label ⇒ no Add. */
      label={highlightedCandidate?.primary ?? null}
      peekCapable={peek?.peekCapable ?? false}
      recipe={peek?.peek ?? null}
      navigable={navigable}
      inBasket={state.highlighted != null && basketIds.has(state.highlighted)}
      blocked={state.highlighted != null && candidates.linked.has(state.highlighted)}
      onAdd={(id, label) => dispatch({ kind: 'toggle', id, label, max })}
      recordId={recordId}
    />
  )

  return (
    <Modal
      label="Add relationship"
      testid="relationship-workspace"
      width={1180}
      className={s.panel}
      onClose={onClose}
      /* Escape respects the dirty-state question: the confirmation is itself a
         modal, so while it is open Escape answers IT, and the workspace behind
         stays exactly as it was. */
      dismissable={state.confirming == null}
    >
      <div className={s.shell} data-staged={staged ? 'yes' : 'no'} data-stage={stage}>
        <header className={s.head}>
          <div className={s.headText}>
            <h2 className={s.title}>Add relationship</h2>
            <p className={s.sub}>
              Pick the relationship type, then link records that already exist or create a new one.
            </p>
            {/* WHERE THE PERSON IS, on the staged face only — three panes at
                once have no steps to count. `aria-current` rather than a
                progress bar: the shape of the flow is three named states, and a
                bar tells a screen reader nothing about which one it is in. */}
            {/* THE KIT'S STEPPER (#2406), in its `compact` presentation:
                numbered nodes with their names in the accessibility tree.
                This was three hand-drawn dots — no done state, and nodes a
                screen reader heard as "1", "2", "3". The caption beside it is
                this dialog's copy, set as the kit's supporting text; on the
                cap both read their ink from the inverse scope `.head`
                declares. */}
            {staged && (
              <div className={s.steps} data-testid="workspace-steps">
                <Steps compact current={WORKSPACE_STAGES.indexOf(stage)}
                  steps={WORKSPACE_STAGES.map((name) => ({ key: name, label: STAGE_LABEL[name] }))} />
                <span className="supporting">
                  Step {WORKSPACE_STAGES.indexOf(stage) + 1} of {WORKSPACE_STAGES.length}
                </span>
              </div>
            )}
            <p className={s.context} data-testid="workspace-context">
              <span className={s.contextSlash}>/</span>
              <strong>{recordLabel ?? 'This record'}</strong>
              {slot && <><span aria-hidden className={s.contextArrow}>→</span><span className={s.contextTarget}>{slot.label}</span></>}
            </p>
          </div>
          <Button variant="ghost" size="sm" className={s.close} onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </Button>
        </header>

        <div className={s.body}>
          <nav className={s.rail} aria-label="Relationship types" data-testid="workspace-rail">
            <SearchField icon={<Search size={14} />} wrapClassName={s.railSearch}
              value={typeQuery} onChange={(e) => setTypeQuery(e.target.value)}
              placeholder="Search types…" aria-label="Search relationship types"
              data-testid="workspace-type-search" />
            {/* The kit's one "nothing here" placeholder (#2406), for all
                six of this dialog's empty states. */}
            {slots.length === 0 && (
              <EmptyState data-testid="workspace-no-types">
                No relationships are registered for this record type yet.
              </EmptyState>
            )}
            {slots.length > 0 && shownGroups.length === 0 && (
              <EmptyState data-testid="workspace-type-no-match">
                No relationship type matches “{typeQuery.trim()}”.
              </EmptyState>
            )}
            {shownGroups.map((group) => (
              <div key={group.key || 'ungrouped'} className={s.railGroup}>
                {group.label && <h3 className={s.railHead}>{group.label}</h3>}
                {/* A DIV, not a UL. `role="listbox"` on a list whose children
                    are `<li>` wrappers fails `aria-required-children`: the
                    listbox's children in the accessibility tree are list items
                    rather than options. Measured as a serious Axe violation in
                    the dark/forced-colour run (#2351). */}
                <div className={s.railList} role="listbox"
                  aria-label={group.label || 'Relationship types'}>
                  {group.slots.map(({ slot: candidate, description, icon }) => {
                    const selected = candidate.id === state.slot
                    const count = countLabel(counts.bySlot.get(candidate.id))
                    const linkable = relationshipWriteOffered(candidate, 'link')
                      || relationshipWriteOffered(candidate, 'create')
                    return (
                      /* THE KIT'S LIST ROW (#2406), as a listbox option. The
                         box, the text block, the hover, the focus ring and the
                         selected tint-and-edge are `ListRow`'s; the option
                         role and its state are this rail's and pass through. */
                      <ListRow
                          key={candidate.id}
                          role="option"
                          aria-selected={selected}
                          aria-disabled={!linkable}
                          selected={selected}
                          className={s.railItem}
                          data-testid={`workspace-type-${candidate.id}`}
                          onClick={() => linkable && dispatch({ kind: 'choose-type', slot: candidate.id })}
                          leading={(
                            <IconTile size="lg">
                              <EntityIcon iconKey={icon ?? iconOf(identities.data, candidate.otherEntity)} size={16} />
                            </IconTile>
                          )}
                          label={candidate.label}
                          detail={<>{candidate.otherLabel}{description ? <> · {description}</> : null}</>}
                          trailing={count != null || !linkable ? (
                            <>
                              {/* A count only where the server GAVE one. An
                                  absent counter is unknown, and drawing it as 0
                                  over rows RLS is hiding invites a duplicate. */}
                              {count != null && (
                                <CountPill tone={selected ? 'brand' : 'outline'}
                                  data-testid={`workspace-count-${candidate.id}`}>{count}</CountPill>
                              )}
                              {/* Bare text: the row sets its trailing words as
                                  metadata, which is all `.railRefused` did. */}
                              {!linkable && 'Not offered'}
                            </>
                          ) : undefined}
                        />
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          <section className={s.work} aria-label="Target records">
            {!slot && (
              <EmptyState data-testid="workspace-choose-type">
                Choose a relationship type to see the records you can link.
              </EmptyState>
            )}
            {slot && !offered.link && !offered.create && (
              <EmptyState role="status" data-testid="workspace-refused">
                {slot.label} cannot be added from here. This relationship offers no link or create
                operation to you.
              </EmptyState>
            )}
            {slot && (offered.link || offered.create) && (
              <>
                <div className={s.modes}>
                  {/* Create new is OFFERED, not disabled, only where the
                      governed contract permits it: a control that exists to be
                      refused is a worse answer than one that is not there. */}
                  <SegmentedControl
                    label="How to add"
                    testId="workspace-mode"
                    value={mode}
                    options={[
                      ...(offered.link ? [{ value: 'link' as const, label: 'Link existing' }] : []),
                      ...(offered.create ? [{ value: 'create' as const, label: 'Create new' }] : []),
                    ]}
                    onChange={(next) => dispatch({ kind: 'mode', mode: next })}
                  />
                  <p className={s.tally} data-testid="workspace-tally">
                    {mode === 'create'
                      ? `New ${identity?.singular_label?.toLowerCase() ?? slot.otherLabel.toLowerCase()}`
                      : tally(candidates, identity?.plural_label ?? slot.otherLabel,
                        identity?.singular_label)}
                  </p>
                </div>

                {mode === 'link' ? (
                  <CandidateList
                    slot={slot}
                    candidates={candidates}
                    request={request}
                    sort={state.sort}
                    max={max}
                    basket={basketIds}
                    highlighted={state.highlighted}
                    onQuery={(query) => set({ query })}
                    onSort={(sort) => dispatch({ kind: 'sort', sort })}
                    onFilter={(patch) => set(patch)}
                    /* HIGHLIGHT IS NOT PREVIEW on touch. This used to open the
                       sheet whenever a row was highlighted, which on a compact
                       viewport meant SELECTING a record popped a modal over the
                       list and made the next tap inert — found by the touch
                       tests, where the second click failed on
                       `pointer-events: none`. The sheet now opens only from the
                       info control, which is the whole reason that control
                       exists. */
                    onHighlight={(id) => dispatch({ kind: 'highlight', id })}
                    onToggle={(candidate) => dispatch({
                      kind: 'toggle', id: candidate.id, label: candidate.primary, max,
                      blocked: candidate.alreadyLinked,
                    })}
                    /* TOUCH HAS NO HOVER, so the preview needs a control of its
                       own — the one real functional gap between the two faces,
                       and #2358 P5's point: replace hover, do not delete it.
                       The trigger is remembered so focus returns to the EXACT
                       glyph the person pressed. */
                    onInspect={(id, trigger) => {
                      dispatch({ kind: 'highlight', id })
                      previewTrigger.current = trigger
                      setPreviewOpen(true)
                    }}
                    showInspect={compact}
                    onMore={more}
                    onCreate={offered.create ? () => dispatch({ kind: 'mode', mode: 'create' }) : undefined}
                  />
                ) : (
                  <div className={s.create} data-testid="workspace-create">
                    <Notice tone="info" className={s.createNote}>
                      Fields come from <strong>{identity?.singular_label ?? slot.otherLabel}</strong>,
                      because that is what “{slot.label}” points at. It will be created and linked
                      to <strong>{recordLabel ?? 'this record'}</strong> in one step.
                    </Notice>
                    {review && (
                      <Notice tone="warn" className={s.review} data-testid="workspace-duplicate-review"
                        role="group" aria-labelledby="workspace-review-title">
                        <h4 id="workspace-review-title">Could this person already exist?</h4>
                        <p>Choose the existing person to link instead. Create another only after checking.</p>
                        <div className={s.reviewList}>
                          {review.candidates.map((candidate) => (
                            <Button key={candidate.id} variant="secondary" className={s.reviewItem}
                              onClick={() => {
                                dispatch({ kind: 'mode', mode: 'link' })
                                dispatch({
                                  kind: 'toggle', id: candidate.id,
                                  label: String(candidate.primary), max,
                                })
                                setReview(null)
                              }}>
                              <strong>{candidate.primary}</strong>
                              {candidate.secondary != null && <span>{candidate.secondary}</span>}
                            </Button>
                          ))}
                        </div>
                      </Notice>
                    )}
                    {/* The footer owns the one primary action, so this form
                        draws none — and the footer submits THROUGH it
                        (`requestSubmit`), which keeps the form's own validation
                        gate rather than growing a second copy of it. */}
                    <Form
                      fields={createFields}
                      value={state.draft}
                      onChange={(patch) => {
                        dispatch({ kind: 'draft', patch })
                        if (review) {
                          const next = { ...state.draft, ...patch }
                          if (personIdentityFingerprint(splitRelatedDraft(next).record) !== review.fingerprint) {
                            setReview(null)
                          }
                        }
                      }}
                      onSubmit={() => { void commit(pendingAnother.current) }}
                      formRef={createFormRef}
                      hideActions
                      variant="quick"
                      layout="grid"
                      busy={busy}
                      testid="workspace-create-form"
                    />
                  </div>
                )}
              </>
            )}
          </section>

          {/* TABLET AND PHONE: the preview is a governed side sheet rather than
              a third column, which is #2351's responsive contract. Desktop
              keeps it in view, because comparing candidates is the job. */}
          {!compact && <aside className={s.preview} aria-label="Preview">{previewPane}</aside>}
        </div>

        <footer className={s.foot}>
          <div className={s.basket} data-testid="workspace-basket">
            <span className={s.basketLabel}>Basket</span>
            {state.basket.length === 0 && (
              <span className={s.basketEmpty}>
                {max === 1
                  ? 'Nothing selected yet — pick one record.'
                  : 'Nothing selected yet — tick records to add several at once.'}
              </span>
            )}
            {state.basket.map((entry) => (
              /* The KIT's value chip: its remove control is `FilterChip`'s own,
                 which is what adopting a control means rather than copying its
                 shape into a host (#2016). */
              <FilterChip key={entry.id} className={s.chip}
                data-testid={`workspace-chip-${entry.id}`}
                onRemove={() => dispatch({ kind: 'remove', id: entry.id })}>
                {entry.label}
              </FilterChip>
            ))}
            {state.basket.length > 0 && (
              <Button variant="ghost" size="sm" className={s.clear}
                data-testid="workspace-basket-clear"
                onClick={() => dispatch({ kind: 'clear' })}>Clear</Button>
            )}
          </div>

          {/* OUTSIDE the strip, and that is load-bearing: the staged face hides
              the chip strip on the first two steps (the count rides on the
              primary there), and a live region inside a hidden element
              announces nothing. Selection state that exists only as a tint is
              not state for everybody. */}
          <span className={s.srOnly} role="status" aria-live="polite" data-testid="workspace-basket-count">
            {state.basket.length === 0
              ? 'No records selected'
              : `${state.basket.length} record${state.basket.length === 1 ? '' : 's'} selected`}
          </span>

          {state.announcement && (
            <Notice tone="warn" live="alert" tabIndex={-1} ref={errorRef}
              data-testid="workspace-announcement">{state.announcement}</Notice>
          )}
          {(guardError || candidates.isError) && (
            <Notice tone="warn" live="alert" data-testid="workspace-error">
              {guardError ?? 'These records could not be read. The search may have been refused.'}
            </Notice>
          )}

          <div className={s.actions}>
            {edgeFields.length > 0 && (
              <div className={s.edge} data-testid="workspace-edge">
                {/* `variant="full"` because that is what makes `layout="grid"`
                    mean anything: `Form` computes `grid` as
                    `layout === 'grid' && variant !== 'quick'`, so `quick` is
                    always one column — which on a short wide viewport grew this
                    footer tall enough to squeeze the candidate list to nothing
                    (measured at 960x480). The mockup draws these facts as a row
                    beside the actions, and now they are one. */}
                <Form
                  fields={edgeFields}
                  value={state.edge}
                  onChange={(patch) => dispatch({ kind: 'edge', patch })}
                  variant="full"
                  layout="grid"
                  hideActions
                  testid="workspace-edge-form"
                />
              </div>
            )}
            {/* THE THUMB BAR (#2358 P1/P4). Back sits at the LEFT, away from
                the sweep where mis-taps happen; Cancel appears only on the
                first step, where there is nothing to lose; and one primary
                carries the step's own truth. Nothing destructive is inside the
                right-hand arc. */}
            {staged ? (
              <div className={s.buttons} data-staged="yes">
                {stage === 'type' ? (
                  <Button size="sm" variant="ghost" onClick={onClose} data-testid="workspace-cancel">
                    Cancel
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" data-testid="workspace-back"
                    onClick={() => dispatch({ kind: 'retreat' })}>
                    <ArrowLeft size={16} aria-hidden /> Back
                  </Button>
                )}
                <Button size="sm" variant="primary" data-testid="workspace-commit"
                  disabled={primary.disabled || busy}
                  onClick={() => (primary.act === 'commit' ? submit(false) : dispatch({ kind: 'advance' }))}>
                  {primary.label}
                </Button>
              </div>
            ) : (
              <div className={s.buttons}>
                <Button size="sm" variant="ghost" onClick={onClose} data-testid="workspace-cancel">Cancel</Button>
                <Button size="sm" variant="secondary" data-testid="workspace-save-another"
                  disabled={!commitReady(state, offered) || busy}
                  onClick={() => submit(true)}>Save &amp; add another</Button>
                <Button size="sm" variant="primary" data-testid="workspace-commit"
                  disabled={!commitReady(state, offered) || busy}
                  onClick={() => submit(false)}>{commitLabel(state)}</Button>
              </div>
            )}
          </div>
        </footer>
      </div>

      {compact && previewOpen && (
        /* `variant="bottom"` on the staged face — #2328's established bottom
           sheet, which is where a thumb expects it — and the right-edge sheet
           on a tablet, where the pane it replaces was a column.

           `restoreFocusTo` is the exact info glyph that opened it. Without it
           focus lands on whatever `document.activeElement` was at MOUNT, and
           for a control inside a list that has already re-rendered, that is
           `<body>` — the #1466 F-30B defect, one surface over. */
        <Modal label="Record preview" variant={staged ? 'bottom' : 'sheet'} width={420}
          testid="workspace-preview-sheet"
          restoreFocusTo={previewTrigger}
          onClose={() => setPreviewOpen(false)}>
          <div className={s.previewSheet}>
            <div className={s.sheetHead}>
              <span className={s.sheetGrip} aria-hidden />
              {/* An EXPLICIT labelled Close, in addition to the scrim: a sheet
                  that can only be dismissed by tapping outside it is a sheet a
                  screen-reader user cannot close (ROOT's correction). */}
              <Button size="sm" variant="ghost" data-testid="workspace-preview-close"
                onClick={() => setPreviewOpen(false)}>
                <X size={16} aria-hidden /> Close
              </Button>
            </div>
            {previewPane}
          </div>
        </Modal>
      )}

      {state.confirming && (
        <Modal label="Discard this work?" width={420} testid="workspace-discard"
          onClose={() => dispatch({ kind: 'keep-editing' })}>
          <div className={s.confirm}>
            <h3>Switch relationship type?</h3>
            <p>
              {state.basket.length > 0
                ? `${state.basket.length} selected record${state.basket.length === 1 ? '' : 's'} and any facts you entered will be discarded.`
                : 'The details you entered will be discarded.'}
            </p>
            <div className={s.confirmActions}>
              <Button size="sm" variant="ghost" data-testid="workspace-keep-editing"
                onClick={() => dispatch({ kind: 'keep-editing' })}>Keep editing</Button>
              <Button size="sm" variant="primary" data-testid="workspace-discard-confirm"
                onClick={() => dispatch({ kind: 'discard-and-switch' })}>Discard and switch</Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  )
}

/* ── the candidate list ─────────────────────────────────────────────────────
 *
 * A LISTBOX, with one tab stop and arrow keys: Up/Down move the highlight
 * (which is what the preview follows), Home/End jump, and Enter or Space
 * selects. Already-linked rows keep their place, are `aria-disabled`, and carry
 * the reason in text that assistive technology reads through `aria-describedby`
 * rather than in a colour.
 */
function CandidateList({
  slot, candidates, request, sort, max, basket, highlighted,
  onQuery, onSort, onFilter, onHighlight, onToggle, onInspect, showInspect, onMore, onCreate,
}: {
  slot: RelationSlot
  candidates: ReturnType<typeof useRelationCandidates>
  request: ReturnType<typeof useCandidateRequest>['request']
  sort: CandidateSort
  max: number | null
  basket: ReadonlySet<string>
  highlighted: string | null
  onQuery: (query: string) => void
  onSort: (sort: CandidateSort) => void
  onFilter: (patch: { hideLinked?: boolean; activeOnly?: boolean }) => void
  onHighlight: (id: string | null) => void
  onToggle: (candidate: { id: string; primary: string; alreadyLinked: boolean }) => void
  /** Open the preview for one candidate, remembering the control that did it. */
  onInspect: (id: string, trigger: HTMLButtonElement) => void
  /** Draw the per-row info affordance: touch has no hover to preview with. */
  showInspect: boolean
  onMore: () => void
  onCreate?: () => void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const items = candidates.items

  const move = (from: number, delta: number) => {
    const next = Math.max(0, Math.min(items.length - 1, from + delta))
    const target = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="row"] > [role="gridcell"]:first-child > button')[next]
    target?.focus()
    const id = items[next]?.id ?? null
    onHighlight(id)
  }

  return (
    <div className={s.candidates}>
      {/* On the staged face this row is ORDERED LAST by the stylesheet, so it
          sits immediately above the primary action and stays above the virtual
          keyboard (#2358 P2). It is the same element in both faces: a second
          search box for the phone would be a second piece of state. */}
      {/* NAMES THE ENTITY, because "Search…" over a list of addresses does
          not say what will be searched. */}
      <SearchField
        icon={<Search size={15} />}
        wrapClassName={s.searchRow}
        value={request.query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder={`Search ${slot.otherLabel.toLowerCase()} records…`}
        aria-label={`Search ${slot.otherLabel.toLowerCase()} records`}
        data-testid="workspace-candidate-search"
      />

      <div className={s.filters}>
        {/* The kit's toggleable pill, which is what these are: `Chip` carries
            the brand-tinted on-state and the `aria-pressed` with it. */}
        <Chip active={request.hideLinked} className={s.toggle}
          data-testid="workspace-hide-linked"
          onClick={() => onFilter({ hideLinked: !request.hideLinked })}>Hide already linked</Chip>
        {/* Offered only where the target entity declares the concept AND the
            branch can filter on it. Absent, not inert. */}
        {candidates.activeOffered && (
          <Chip active={request.activeOnly} className={s.toggle}
            data-testid="workspace-active-only"
            onClick={() => onFilter({ activeOnly: !request.activeOnly })}>Active only</Chip>
        )}
        {/* One option is not a choice: a door ranks its own rows and has
            nothing to switch between, so no control is drawn. */}
        {candidates.sorts.length > 1 && (
          <div className={s.sort}>
            <Field label="Sort">
              <OptionPicker
                value={sort}
                onChange={(next) => onSort(next as CandidateSort)}
                options={candidates.sorts.map((key) => ({ value: key, label: CANDIDATE_SORT_LABELS[key] }))}
                testId="workspace-sort"
              />
            </Field>
          </div>
        )}
      </div>

      {candidates.linkedUnknown && (
        <p className={s.hint} role="status" data-testid="workspace-linked-unknown">
          Existing links could not be read, so nothing is hidden as already linked.
        </p>
      )}

      {candidates.branch === 'none' && (
        <EmptyState data-testid="workspace-no-search">
          {slot.otherLabel} records cannot be searched from here: this deployment registers no
          directory for them.
        </EmptyState>
      )}

      {candidates.isLoading && items.length === 0 && (
        <p className={s.hint} data-testid="workspace-candidates-loading">Looking…</p>
      )}

      {!candidates.isLoading && !candidates.isError && items.length === 0 && candidates.branch !== 'none' && (
        <EmptyState data-testid="workspace-candidates-empty"
          /* The no-results create affordance #2351 requires, and only where
             creating is eligible. It seeds the canonical display field and
             nothing else — and it is the empty state's own action slot. */
          action={onCreate && request.query.trim() ? (
            <Button size="sm" variant="secondary" data-testid="workspace-create-from-query"
              onClick={onCreate}>
              <Plus size={14} aria-hidden /> Create “{request.query.trim()}”
            </Button>
          ) : undefined}>
          {request.query.trim()
            ? `Nothing matches “${request.query.trim()}”.`
            : `No ${slot.otherLabel.toLowerCase()} records are available to link.`}
        </EmptyState>
      )}

      {/* A GRID, not a listbox. A listbox option may not contain interactive
          content, and these rows carry two controls — selection and the info
          affordance touch needs. axe reported the listbox shape as a CRITICAL
          `aria-required-children` twice: once for the `<li>` wrappers, and
          again for the row wrapper the second control needs. The grid pattern
          is the one built for a selectable row with a row action, and it says
          more rather than less: the row carries `aria-selected`, each control
          is in its own cell. */}
      <div className={s.list} role="grid" ref={listRef}
        aria-multiselectable={max !== 1}
        aria-label={`${slot.otherLabel} records`} data-testid="workspace-candidates">
        {items.map((candidate, index) => {
          const selected = basket.has(candidate.id)
          const reasonId = `workspace-reason-${candidate.id}`
          return (
            <div key={candidate.id} className={s.rowWrap} role="row"
              aria-selected={selected}
              /* On the ROW as well as on its button, and both are true: the row
                 is not selectable and the control is not actionable. A grid row
                 is what a screen reader lands on, so it is where the refusal
                 has to be readable. */
              aria-disabled={candidate.alreadyLinked || undefined}
              data-testid={`workspace-row-${candidate.id}`}>
              <span role="gridcell" className={s.rowCell}>
              {/* THE KIT'S LIST ROW (#2406), as the grid cell's selection
                  control — the same row the type rail and the recall panel
                  draw. The mark, the highlight and the state words are this
                  list's; the box and the text block are the kit's. */}
              <ListRow
                aria-disabled={candidate.alreadyLinked}
                aria-describedby={candidate.alreadyLinked || candidate.inactive ? reasonId : undefined}
                tabIndex={index === Math.max(0, items.findIndex((i) => i.id === highlighted)) ? 0 : -1}
                selected={selected}
                className={s.candidate}
                data-linked={candidate.alreadyLinked ? 'yes' : 'no'}
                data-highlighted={candidate.id === highlighted ? 'yes' : 'no'}
                data-testid={`workspace-candidate-${candidate.id}`}
                onFocus={() => onHighlight(candidate.id)}
                onClick={() => onToggle(candidate)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); move(index, 1) }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); move(index, -1) }
                  else if (e.key === 'Home') { e.preventDefault(); move(0, 0) }
                  else if (e.key === 'End') { e.preventDefault(); move(items.length - 1, 0) }
                }}
                leading={(
                  <span className={s.rowMark} data-kind={max === 1 ? 'radio' : 'check'}>
                    {selected ? '✓' : ''}
                  </span>
                )}
                label={candidate.primary}
                detail={candidate.secondary}
                trailing={candidate.alreadyLinked || candidate.inactive ? (
                  /* STATES IN WORDS, as the kit's `Pill` (#2406) — which can
                     be one now that its `id` reaches the element for the
                     row's `aria-describedby`. The tone is never the only
                     carrier. */
                  candidate.alreadyLinked ? (
                    <Pill id={reasonId}
                      data-testid={`workspace-linked-${candidate.id}`}>Already linked</Pill>
                  ) : (
                    <Pill tone="warn" id={reasonId}
                      data-testid={`workspace-inactive-${candidate.id}`}>Inactive</Pill>
                  )
                ) : undefined}
              />
              </span>
              {/* Its own CELL. A row action shares the row with the selection
                  control and cannot be inside it — small glyph, 44px hit
                  target (ROOT's correction), and an accessible name that says
                  which record it previews. */}
              {showInspect && (
              <span role="gridcell" className={s.rowCell}>
                <Button
                  variant="secondary"
                  size="sm"
                  className={s.inspect}
                  data-testid={`workspace-inspect-${candidate.id}`}
                  aria-label={`Preview ${candidate.primary}`}
                  onClick={(e) => onInspect(candidate.id, e.currentTarget as HTMLButtonElement)}
                >
                  <Info size={18} aria-hidden />
                </Button>
              </span>
              )}
            </div>
          )
        })}
      </div>

      {candidates.more && (
        <div className={s.moreRow}>
          <Button size="sm" variant="ghost" data-testid="workspace-show-more" onClick={onMore}>
            Show more
          </Button>
          <span className={s.hint}>
            {candidates.total == null
              ? `Showing the first ${items.length} matches — narrow the search to see fewer.`
              : `Showing ${items.length} of ${candidates.total}.`}
          </span>
        </div>
      )}
    </div>
  )
}

/* ── the preview ─────────────────────────────────────────────────────────── */

function PreviewPane({
  slot, id, label, peekCapable, recipe, navigable, inBasket, blocked, onAdd, recordId,
}: {
  slot: RelationSlot | null
  id: string | null
  label: string | null
  peekCapable: boolean
  recipe: Parameters<typeof PeekRow>[0]['recipe']
  navigable: boolean
  inBasket: boolean
  blocked: boolean
  onAdd: (id: string, label: string) => void
  recordId: string
}) {
  const { client } = useSolum()

  /* "ALREADY CONNECTED TO" — one governed hop from the CANDIDATE, through the
     same door the whole-record graph uses. Its failure is silent about the
     record and explicit about itself: an unreadable hop is said out loud, never
     drawn as "connected to nothing". */
  const hop = useQuery({
    queryKey: ['relation-preview-hop', slot?.otherEntity ?? '', id ?? ''],
    enabled: !!slot && !!id,
    staleTime: 60_000,
    queryFn: () => client.raw().rpcScalar<GraphHop>('relationship_graph_hop', {
      p_entity: slot!.otherEntity, p_id: id, p_groups: null, p_limit: 6, p_cursor: null,
    }, 'core'),
  })

  if (!slot || !id) {
    return (
      <EmptyState className={s.previewEmpty} data-testid="workspace-preview-empty">
        <Search size={20} aria-hidden />
        <p>Highlight a record to preview it before linking.</p>
      </EmptyState>
    )
  }

  const connected = (hop.data?.nodes ?? [])
    .filter((node) => node.id !== id && node.id !== recordId && node.label)
    .slice(0, 4)

  return (
    <div className={s.previewBody} data-testid="workspace-preview">
      <h3 className={s.previewHead}>Preview</h3>
      {peekCapable ? (
        <PeekRow entity={slot.otherEntity} id={id} recipe={recipe} testId="workspace-peek" />
      ) : (
        /* EXPLICIT, never fabricated. A target with no canonical readable form
           has no preview, and #2281's rule is that the lens is withdrawn rather
           than replaced with a local field list. */
        <EmptyState data-testid="workspace-preview-unavailable">
          This record has no preview here: {slot.otherLabel} declares no readable form for it.
        </EmptyState>
      )}

      <div className={s.connected}>
        <h4>Already connected to</h4>
        {hop.isError && <p data-testid="workspace-connected-unavailable">Connections could not be read.</p>}
        {!hop.isError && hop.isLoading && <p>Looking…</p>}
        {!hop.isError && !hop.isLoading && connected.length === 0 && (
          <p data-testid="workspace-connected-none">Nothing else yet.</p>
        )}
        <div className={s.connectedChips}>
          {connected.map((node) => (
            <Pill key={node.key}>{node.label}</Pill>
          ))}
        </div>
      </div>

      <div className={s.previewActions}>
        {/* Offered only where the far entity IS a navigable record (#1993). */}
        {navigable && (
          <a className={s.openLink} href={`/e/${slot.otherEntity}/${id}`} target="_blank"
            rel="noreferrer" data-testid="workspace-open-new-tab">Open in new tab</a>
        )}
        {/* `In basket` is a STATE, not a second way to remove: a button that
            says where a record IS and takes it out when pressed is a label
            that lies about its own action. Removal lives on the chip and on
            the row, each of which says so. */}
        <Button size="sm" variant={inBasket ? 'secondary' : 'primary'}
          disabled={blocked || inBasket || !label}
          data-testid="workspace-preview-add"
          onClick={() => label && onAdd(id, label)}>
          {blocked ? 'Already linked' : inBasket ? 'In basket' : 'Add'}
        </Button>
      </div>
    </div>
  )
}

/* ── the derivations this file owns ─────────────────────────────────────── */

/**
 * The EDGE's own facts: the relationship type's declared attributes, plus the
 * one lifecycle fact the write door actually accepts.
 *
 * `valid_from` and nothing else, measured rather than assumed:
 * `core.relationship_write_generic_link` inserts `attributes` and `valid_from`
 * (core 2026091522) and never `valid_to`, and the governed `located_at`
 * contract maps `valid_from` on create and link while `valid_to` belongs to its
 * `end` operation. Offering an end date at link time would collect a value the
 * door drops — so the field is absent and ending a relationship stays the verb
 * that ends it.
 */
export function edgeFactFields(slot: RelationSlot | null): ReturnType<typeof relationshipAttributeFields> {
  if (!slot || slot.storage !== 'edge') return []
  return [
    { name: 'valid_from', type: 'date', label: 'Valid from', hint: 'Optional' },
    ...relationshipAttributeFields(slot),
  ]
}

/** The declared attributes, separated from the lifecycle fact beside them. */
export function edgeAttributes(
  edge: Record<string, unknown>,
  fields: ReturnType<typeof relationshipAttributeFields>,
): Row {
  const out: Row = {}
  for (const field of fields) {
    if (field.name === 'valid_from') continue
    const value = edge[field.name]
    if (value != null && value !== '') out[field.name] = value
  }
  return out
}

/**
 * The create form: the target entity's own canonical quick-create set, with its
 * reference columns wired into real pickers exactly as the drawer wires them.
 *
 * Deliberately the SAME derivation — `quickCreateFields` and `wireRelations`,
 * both imported rather than reimplemented — because a create-in-flow with its
 * own field list is where a required field quietly stops being required and a
 * `ref` column becomes a raw-id input. The EDGE's facts are not here: they live
 * once, in the footer, and apply to the created edge as they do to a linked one.
 */
function useCreateFormFields(
  slot: RelationSlot | null,
  fields: Parameters<typeof quickCreateFields>[0],
  draft: Record<string, unknown>,
) {
  const schema = useSchema()
  const allSemantics = useSemantics()
  const { client } = useSolum()
  const queryClient = useQueryClient()
  return useMemo(() => {
    if (!slot) return []
    return wireRelations(
      quickCreateFields(fields), slot.otherEntity, schema.data, client.raw(), client,
      queryClient, allSemantics.data?.fields ?? [], draft)
  }, [slot, fields, schema.data, client, queryClient, allSemantics.data, draft])
}

const iconOf = (
  identities: { entity_type: string; icon_key: string | null }[] | undefined,
  entity: string,
): string | null => identities?.find((e) => e.entity_type === entity)?.icon_key ?? null

/**
 * The count line — and it never turns an unavailable total into a number.
 *
 * The door branch reports no total at all, so it says how many are SHOWING;
 * the table branch says "N of M" because M came from the server.
 */
function tally(
  candidates: ReturnType<typeof useRelationCandidates>,
  plural: string,
  singular?: string | null,
): string {
  const shown = candidates.items.length
  /* `1 people` is what a plural label alone produces, and the governed registry
     has BOTH nouns — `core.entity_identity` carries `singular_label` beside
     `plural_label`, so the count does not have to guess or pluralise. Caught in
     the governed raster of this surface. */
  const noun = (shown === 1 && candidates.total == null ? (singular ?? plural) : plural).toLowerCase()
  if (candidates.isError) return `${noun} could not be read`
  if (candidates.total != null) {
    const total = candidates.total === 1 ? (singular ?? plural).toLowerCase() : plural.toLowerCase()
    return `${shown} of ${candidates.total} ${total}`
  }
  return candidates.more ? `first ${shown} ${noun}` : `${shown} ${noun}`
}
